// Copyright (c) 2023-present VexFlow contributors: https://github.com/vexflow/vexflow/graphs/contributors
// MIT License
import { Bend } from './bend';
import { Modifier } from './modifier';
import { Tables } from './tables';
import { Category } from './typeguard';
import { RuntimeError } from './util';
/** `Vibrato` implements diverse vibratos. */
export class Vibrato extends Modifier {
    static get CATEGORY() {
        return Category.Vibrato;
    }
    /** Arrange vibratos inside a `ModifierContext`. */
    static format(vibratos, state, context) {
        if (!vibratos || vibratos.length === 0)
            return false;
        // Vibratos are always on top.
        let textLine = state.topTextLine;
        let width = 0;
        let shift = state.rightShift - 7;
        // If there's a bend, drop the text line
        const bends = context.getMembers(Bend.CATEGORY);
        if (bends && bends.length > 0) {
            const bendHeight = bends.map((bb) => bb.getTextHeight()).reduce((a, b) => (a > b ? a : b)) / Tables.STAVE_LINE_DISTANCE;
            textLine = textLine - (bendHeight + 1);
        }
        else {
            state.topTextLine += 1;
        }
        // Format Vibratos
        for (let i = 0; i < vibratos.length; ++i) {
            const vibrato = vibratos[i];
            vibrato.setXShift(shift);
            vibrato.setTextLine(textLine);
            width += vibrato.getWidth();
            shift += width;
        }
        state.rightShift += width;
        return true;
    }
    constructor() {
        super();
        this.position = Modifier.Position.RIGHT;
        this.renderOptions = {
            code: 0xeab0,
            width: 20,
        };
        this.setVibratoWidth(this.renderOptions.width);
    }
    /** Set vibrato width in pixels. */
    setVibratoWidth(width) {
        this.renderOptions.width = width;
        this.text = String.fromCodePoint(this.renderOptions.code);
        const myWidth = this.getWidth();
        if (!myWidth) {
            throw new RuntimeError('Cannot set vibrato width if width is 0');
        }
        const items = Math.round(this.renderOptions.width / myWidth);
        for (let i = 1; i < items; i++) {
            this.text += String.fromCodePoint(this.renderOptions.code);
        }
        return this;
    }
    /** Set vibrato code. */
    setVibratoCode(code) {
        this.renderOptions.code = code;
        return this.setVibratoWidth(this.renderOptions.width);
    }
    /** Draw the vibrato on the rendering context. */
    draw() {
        const ctx = this.checkContext();
        const note = this.checkAttachedNote();
        this.setRendered();
        const start = note.getModifierStartXY(Modifier.Position.RIGHT, this.index);
        const vx = start.x;
        const vy = note.getYForTopText(this.textLine) + 5;
        this.renderText(ctx, vx, vy);
    }
}
